import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Shield,
  Users,
  BookOpen,
  Settings,
  CreditCard,
  Search
} from "lucide-react";
import Header from "../../mainpagecomponents/Header";
import Footer from "../../mainpagecomponents/Footer";
import { Button } from "../../ui/button";

const FAQPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const faqData = [
    {
      id: 1,
      category: "general",
      question: "What is Gradely?",
      answer: "Gradely is a teacher-centered platform designed to help you assess student learning using BC's Proficiency Scale and Depth of Knowledge (DOK) levels. It helps you plan, assess, and generate feedback efficiently—while keeping you in control."
    },
    {
      id: 2,
      category: "general",
      question: "Who can use Gradely?",
      answer: "Currently, Gradely is built for teachers, especially those working in British Columbia or any school system using DOK levels and proficiency scales. Future updates may expand user roles."
    },
    {
      id: 3,
      category: "general",
      question: "How do I sign up for Gradely?",
      answer: "You can sign in using your Gmail ID or Microsoft ID only. Gradely uses secure Google & Microsoft authentication to simplify access and maintain your privacy."
    },
    {
      id: 4,
      category: "privacy",
      question: "Is my data secure on Gradely?",
      answer: "Yes. All data is securely stored on Microsoft Azure servers located in Canada, in full compliance with Canadian privacy laws."
    },
    {
      id: 5,
      category: "privacy",
      question: "Does Gradely share my data with third parties?",
      answer: "No. Gradely does not sell or share your data. It is used only to support your work as an educator."
    },
    {
      id: 6,
      category: "teaching",
      question: "How does Gradely help with assessment?",
      answer: "Gradely allows you to assess students using DOK levels and BC's proficiency scale. You can input assessments per unit and use AI to generate detailed feedback for individual students or whole classes."
    },
    {
      id: 7,
      category: "teaching",
      question: "Can I generate comments or feedback automatically?",
      answer: "Yes! Once you've recorded assessments, Gradely's AI can generate detailed feedback comments that align with your assessments. These can be copied into report cards or shared with families."
    },
    {
      id: 8,
      category: "teaching",
      question: "What is the difference between DOK and proficiency scale?",
      answer: "DOK (Depth of Knowledge) measures the complexity of student thinking required. Proficiency Scale describes how well students have learned the content (Emerging, Developing, Proficient, Extending)."
    },
    {
      id: 9,
      category: "teaching",
      question: "Does Gradely support all subjects?",
      answer: "Yes. You can assess, plan, and create resources for any subject and any topic, including science, math, language arts, and social studies."
    },
    {
      id: 10,
      category: "planning",
      question: "Can Gradely generate lesson plans and unit plans?",
      answer: "Yes. You can generate unit plans, lesson plans, worksheets, rubrics, and even custom student activities aligned to your chosen topic and grade level."
    },
    {
      id: 11,
      category: "planning",
      question: "Can I customize AI-generated content?",
      answer: "Absolutely. All plans, rubrics, and resources can be edited and tailored to suit your students' needs."
    },
    {
      id: 12,
      category: "students",
      question: "Can I add students and track their progress?",
      answer: "Yes. You can add students, track their assessments, and generate reports showing their growth by DOK level and proficiency scale."
    },
    {
      id: 13,
      category: "students",
      question: "Can I generate progress reports?",
      answer: "Yes. You can create student progress reports based on the DOK assessments."
    },
    {
      id: 14,
      category: "students",
      question: "Is student data shared or public?",
      answer: "No. Student data is private by default. You control when and how progress reports are shared with students or families."
    },
    {
      id: 15,
      category: "technical",
      question: "What devices can I use Gradely on?",
      answer: "Gradely works on desktops, laptops, tablets, and smartphones. It is recommended to use Gradely on laptop or tablet for best experience."
    },
    {
      id: 16,
      category: "technical",
      question: "I forgot my password. What do I do?",
      answer: "Since Gradely uses Gmail & Microsoft login, you'll need to reset your password through Google or Microsoft."
    },
    {
      id: 17,
      category: "technical",
      question: "Who do I contact for support?",
      answer: "Email us at support@gradely.ca or use the \"Help\" button inside the app."
    },
    {
      id: 18,
      category: "account",
      question: "Can I deactivate my account?",
      answer: "Yes. You can delete your account at any time. Please note that deleting your account will permanently remove all your data, and this action cannot be reversed."
    },
    {
      id: 19,
      category: "account",
      question: "Can I pause or cancel my subscription?",
      answer: "Yes. You can cancel your subscription by logging in, navigating to Subscription > Manage Subscription, and following the prompts."
    },
    {
      id: 20,
      category: "account",
      question: "Does Gradely save my payment information?",
      answer: "No. Gradely does not store your payment information on its servers. All payments are securely processed through Stripe, a leading and trusted third-party payment provider used by companies such as OpenAI, Google, and Apple."
    },
    {
      id: 21,
      category: "account",
      question: "Can I get a refund on my subscription?",
      answer: "Refunds are not automatically processed. However, if you have canceled your subscription, you can email us at support@gradely.ca or use the Support button within the platform. Our team will review your request and do our best to assist you."
    }
  ];

  const categories = [
    { id: "all", label: "All Questions", icon: HelpCircle },
    { id: "general", label: "General", icon: BookOpen },
    { id: "privacy", label: "Privacy & Security", icon: Shield },
    { id: "teaching", label: "Teaching & Assessment", icon: Users },
    { id: "planning", label: "Planning Tools", icon: BookOpen },
    { id: "students", label: "Student Management", icon: Users },
    { id: "technical", label: "Technical", icon: Settings },
    { id: "account", label: "Account & Billing", icon: CreditCard }
  ];

  const filteredFAQs = faqData.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === "all" || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-cream">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-primary to-white pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <motion.h1
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary mb-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Frequently Asked Questions
            </motion.h1>
            <motion.p
              className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              Find answers to common questions about Gradely, our features, and how to get the most out of your teaching experience.
            </motion.p>

            {/* Search Bar */}
            <motion.div
              className="relative max-w-md mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-700"
              />
            </motion.div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-8 bg-white">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {categories.map((category, index) => (
                <motion.button
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${activeCategory === category.id
                    ? "bg-primary text-white shadow-lg"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                >
                  <category.icon className="w-4 h-4" />
                  {category.label}
                </motion.button>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Items */}
        <section className="py-16 bg-white">
          <div className="container mx-auto max-w-4xl px-4">
            {filteredFAQs.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <HelpCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No FAQs found</h3>
                <p className="text-gray-500">Try adjusting your search or category filter.</p>
              </motion.div>
            ) : (
              <div className="space-y-4">
                {filteredFAQs.map((faq, index) => (
                  <motion.div
                    key={faq.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    className="bg-cream rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleItem(faq.id)}
                      className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 className="text-lg font-semibold text-primary pr-4">
                        {faq.question}
                      </h3>
                      {openItems[faq.id] ? (
                        <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      )}
                    </button>

                    <motion.div
                      initial={false}
                      animate={{
                        height: openItems[faq.id] ? "auto" : 0,
                        opacity: openItems[faq.id] ? 1 : 0
                      }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-5">
                        <div className="border-t border-gray-200 pt-4">
                          <p className="text-gray-700 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 bg-gradient-to-br from-primary/5 to-secondary/10">
          <div className="container mx-auto max-w-4xl px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-4">
                Still Have Questions?
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                Can't find the answer you're looking for? Our support team is here to help you succeed.
              </p>

              <div className="bg-cream-50 rounded-lg p-6 max-w-md mx-auto">
                <div className="flex items-center justify-center mb-4">
                  <HelpCircle className="w-5 h-5 text-gray-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-700">Get Direct Support</h3>
                </div>

                <div className="text-center">
                  <p className="text-gray-600 text-sm mb-4">
                    Contact our dedicated support team:
                  </p>

                  <div className="mb-4">
                    <p className="text-lg font-semibold text-gray-800 mb-1">
                      support@gradely.ca
                    </p>
                    <p className="text-sm text-green-600 flex items-center justify-center">
                      Response within 24 hours
                    </p>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-sm text-gray-700">
                      <span className="inline-flex items-center mr-1">
                        <span className="text-yellow-600">📧</span>
                      </span>
                      <strong>Important:</strong> Please check your <strong>Spam/Junk folder</strong> as our emails may occasionally be directed there.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;